'use client';

import { useState, useRef } from 'react';
import Image from 'next/image';
import { motion, AnimatePresence, useScroll, useMotionValueEvent } from 'framer-motion';
import { UserPlus, Gift, ScanLine } from 'lucide-react';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

function cn(...inputs: ClassValue[]) {
      return twMerge(clsx(inputs));
}

const steps = [
      {
            id: 0,
            title: 'Gyno Club\'a Katıl',
            description: 'Hızlıca profilini oluştur, sana özel ayrıcalıklar dünyasının kapılarını arala.',
            icon: UserPlus,
            screen: '/Anasayfa.png',
      },
      {
            id: 1,
            title: 'Ayrıcalıkları Keşfet',
            description: 'Yüzlerce markadaki sana özel indirimleri, etkinlikleri ve fırsatları incele.',
            icon: Gift,
            screen: '/kategorilerAkordiyon.png',
      },
      {
            id: 2,
            title: 'Sadece QR\'ını Okut ve Keyfini Çıkar',
            description: 'Anlaşmalı noktalarda dijital kimliğini göster, anında ayrıcalıklardan yararlan.',
            icon: ScanLine,
            screen: '/gyno-life.png',
      },
];

const HowItWorksSection = () => {
      const containerRef = useRef<HTMLDivElement>(null);
      const [activeStep, setActiveStep] = useState(0);

      const { scrollYProgress } = useScroll({
            target: containerRef,
            offset: ["start start", "end end"]
      });

      useMotionValueEvent(scrollYProgress, "change", (latest) => {
            if (window.innerWidth < 768) return;

            if (latest < 0.33) {
                  setActiveStep(0);
            } else if (latest < 0.66) {
                  setActiveStep(1);
            } else {
                  setActiveStep(2);
            }
      });

      const handleStepClick = (index: number) => {
            setActiveStep(index);

            if (window.innerWidth >= 768 && containerRef.current) {
                  const sectionTop = containerRef.current.offsetTop;
                  const sectionHeight = containerRef.current.offsetHeight;
                  const targetScroll = sectionTop + (index * (sectionHeight / 3));
                  window.scrollTo({
                        top: targetScroll + 50,
                        behavior: 'smooth'
                  });
            }
      };

      return (
            <section ref={containerRef} className="relative w-full h-auto md:h-[300vh] bg-[#FAFAFA] py-20 md:py-0">

                  <div className="relative md:sticky top-0 h-auto md:h-screen flex flex-col items-center justify-center overflow-hidden">

                        <div className="absolute left-[-10%] top-[20%] w-125 h-125 bg-purple-400/20 rounded-full blur-[100px] pointer-events-none" />
                        <div className="absolute right-[-10%] bottom-[10%] w-125 h-125 bg-pink-400/20 rounded-full blur-[100px] pointer-events-none" />

                        <div className="container mx-auto px-4 md:px-6 relative z-10 h-full flex flex-col justify-center">

                              <div className="flex flex-col lg:flex-row items-center justify-center gap-12 lg:gap-24">

                                    <div className="relative w-80 h-162.5 sm:w-85 sm:h-172.5 shrink-0 select-none">

                                          <div className="absolute inset-0 z-20 pointer-events-none">
                                                <Image
                                                      src="/iphone17.png"
                                                      alt="iPhone Frame"
                                                      fill
                                                      className="object-contain drop-shadow-2xl"
                                                      priority
                                                />
                                          </div>

                                          <div className="absolute top-4 bottom-4 left-4.75 right-4.75 z-10 rounded-[48px] overflow-hidden bg-black">
                                                <AnimatePresence mode="wait">
                                                      <motion.div
                                                            key={activeStep}
                                                            initial={{ opacity: 0, scale: 1.05 }}
                                                            animate={{ opacity: 1, scale: 1 }}
                                                            exit={{ opacity: 0 }}
                                                            transition={{ duration: 0.4 }}
                                                            className="absolute inset-0 w-full h-full bg-white"
                                                      >
                                                            <div className="relative w-full h-full">
                                                                  <Image
                                                                        src={steps[activeStep].screen}
                                                                        alt={steps[activeStep].title}
                                                                        fill
                                                                        className="object-cover object-top"
                                                                        priority
                                                                  />
                                                            </div>
                                                      </motion.div>
                                                </AnimatePresence>
                                                <div className="absolute inset-0 bg-linear-to-tr from-white/10 via-transparent to-transparent pointer-events-none z-20" />
                                          </div>
                                    </div>

                                    <div className="flex flex-col gap-6 max-w-lg w-full">
                                          <div className="mb-2 text-center lg:text-left">
                                                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                                                      Nasıl Çalışır?
                                                </h2>
                                                <p className="text-gray-500">
                                                      Sadece 3 adımda ayrıcalıklar dünyasına katılın.
                                                </p>
                                          </div>

                                          {steps.map((step, index) => {
                                                const isActive = index === activeStep;
                                                return (
                                                      <div
                                                            key={step.id}
                                                            onClick={() => handleStepClick(index)}
                                                            className={cn(
                                                                  "group relative flex items-start gap-5 p-6 rounded-3xl transition-all duration-300 border cursor-pointer",
                                                                  isActive
                                                                        ? "bg-white border-purple-100 shadow-[0_10px_30px_-10px_rgba(99,32,238,0.15)] scale-100 md:scale-105"
                                                                        : "bg-transparent border-transparent hover:bg-white/40 hover:scale-[1.02]"
                                                            )}
                                                      >
                                                            <div className={cn(
                                                                  "flex items-center justify-center w-14 h-14 rounded-2xl shrink-0 transition-colors duration-300",
                                                                  isActive
                                                                        ? "bg-linear-to-br from-[#6320EE] to-[#DA38E2] text-white shadow-lg shadow-purple-500/30"
                                                                        : "bg-white text-gray-600 border border-gray-200"
                                                            )}>
                                                                  <step.icon className="w-6 h-6" />
                                                            </div>

                                                            <div className="flex flex-col pt-1">
                                                                  <h3 className={cn(
                                                                        "text-lg font-bold mb-1 transition-colors",
                                                                        isActive ? "text-gray-900" : "text-gray-800"
                                                                  )}>
                                                                        {step.title}
                                                                  </h3>
                                                                  <p className={cn(
                                                                        "text-sm leading-relaxed",
                                                                        isActive ? "text-gray-600" : "text-gray-500"
                                                                  )}>
                                                                        {step.description}
                                                                  </p>
                                                            </div>

                                                            {isActive && (
                                                                  <motion.div
                                                                        layoutId="active-step-indicator"
                                                                        className="absolute left-0 top-1/2 -translate-y-1/2 w-1.5 h-16 bg-[#6320EE] rounded-r-full hidden lg:block"
                                                                  />
                                                            )}
                                                      </div>
                                                );
                                          })}
                                    </div>

                              </div>
                        </div>
                  </div>
            </section>
      );
};

export default HowItWorksSection;